import React from 'react';
import { Circle, Ellipse, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Equivalent round duct: rect (a × b) ≡ round (Dₑ) — equal friction loss. */
export function DiagramEquivalentRoundDuct() {
  // Rect (left)
  const rx = 35, ry = 80, rw = 110, rh = 90;
  const rcx = rx + rw / 2, rcy = ry + rh / 2;

  // Round (right)
  const cx = 270, cy = 125, R = 50;

  return (
    <DiagramCanvas>
      {/* Rect cross-section */}
      <Rect x={rx} y={ry} width={rw} height={rh} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {Array.from({ length: 7 }).map((_, i) => {
        const t = (i + 1) / 8;
        return (
          <Line key={i} x1={rx + 4} y1={ry + t * rh} x2={rx + rw - 4} y2={ry + t * rh}
            stroke={DT.varColor} strokeWidth="0.5" opacity={0.3} />
        );
      })}

      {/* a (width) */}
      <Line x1={rx} y1={ry + rh + 14} x2={rx + rw} y2={ry + rh + 14} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx} y1={ry + rh + 10} x2={rx} y2={ry + rh + 18} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx + rw} y1={ry + rh + 10} x2={rx + rw} y2={ry + rh + 18} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={rcx} y={ry + rh + 25} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">a (in)</SvgText>

      {/* b (height) */}
      <Line x1={rx - 14} y1={ry} x2={rx - 14} y2={ry + rh} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx - 18} y1={ry} x2={rx - 10} y2={ry} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx - 18} y1={ry + rh} x2={rx - 10} y2={ry + rh} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={rx - 20} y={rcy + 3} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="end">b</SvgText>

      {/* Equivalence arrow */}
      <Line x1={rx + rw + 22} y1={rcy} x2={cx - R - 22} y2={cy} stroke={DT.leader} strokeWidth="1.5" />
      <Polygon points={`${cx - R - 22},${cy} ${cx - R - 28},${cy - 4} ${cx - R - 28},${cy + 4}`} fill={DT.leader} />
      <SvgText x={(rx + rw + cx - R) / 2} y={rcy - 8} fill={DT.varColor}
        fontSize="14" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">≡</SvgText>
      <SvgText x={(rx + rw + cx - R) / 2} y={rcy + 22} fill={DT.desc}
        fontSize="8" fontFamily={DT.mono} textAnchor="middle">equal ΔP</SvgText>
      <SvgText x={(rx + rw + cx - R) / 2} y={rcy + 32} fill={DT.desc}
        fontSize="8" fontFamily={DT.mono} textAnchor="middle">equal CFM</SvgText>

      {/* Round duct */}
      <Ellipse cx={cx} cy={cy} rx={R} ry={R} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {[0.3, 0.55, 0.8].map((f, i) => (
        <Circle key={i} cx={cx} cy={cy} r={R * f} fill="none"
          stroke={DT.varColor} strokeWidth="0.5" opacity={0.3} />
      ))}
      <Line x1={cx - R} y1={cy} x2={cx + R} y2={cy} stroke={DT.dim} strokeWidth="1" strokeDasharray="3,3" />
      <Polygon points={`${cx - R},${cy} ${cx - R + 6},${cy - 3} ${cx - R + 6},${cy + 3}`} fill={DT.dim} />
      <Polygon points={`${cx + R},${cy} ${cx + R - 6},${cy - 3} ${cx + R - 6},${cy + 3}`} fill={DT.dim} />

      {/* Labels */}
      <LeaderLine x1={rcx} y1={ry + 8} x2={rcx} y2={50} varName="rect" desc="a × b duct" anchor="middle" />
      <LeaderLine x1={cx} y1={cy - R + 8} x2={cx + 30} y2={50} varName="Dₑ" desc="Equiv. diameter (in)" anchor="middle" />
      <LeaderLine x1={cx} y1={cy + R - 8} x2={cx + 60} y2={210} varName="Dₑ" desc="same friction loss as rect" anchor="end" />

      <FormulaTag formula="Dₑ = 1.30 · (a·b)^0.625 / (a+b)^0.25" />
    </DiagramCanvas>
  );
}
